import { Component, OnInit, EventEmitter, TemplateRef, ViewChild, AfterContentChecked } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { switchMap, filter, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Audit, Capa, CapaECTypes, Finding, Evidence, CapaStatus, TableDataEvidence } from '../../audit.model';
import { BreadcrumbItem, TableData, TableOptions, RequestResult } from '../../../shared/shared.model';
import { AuditService } from '../../audit.service';
import { FindingService } from '../../finding.service';
import { CapaService } from '../../capa.service';
import { DatePipe } from '@angular/common';
import { DISPLAY_DATE_FORMAT } from '../../../../app/app.constants';
import { MatDialog } from '@angular/material';
import { EvidenceAddDialogComponent } from '../evidence-add-dialog/evidence-add-dialog.component';
import { EvidenceService } from '../../evidence.service';
import { EVIDENCE_URL } from '../../../app.constants';
import { OkCancelDialogComponent } from 'src/app/shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import { SidService } from '../../sid.service';
import { Sid } from '../../sid.model';

@Component({
  selector: 'iaud-evidence-list',
  templateUrl: './evidence-list.component.html',
  styleUrls: ['./evidence-list.component.scss']
})
export class EvidenceListComponent implements OnInit, AfterContentChecked {

  @ViewChild('fileCellTemplate') fileCellTemplate: TemplateRef<any>;

  evUrl = EVIDENCE_URL;

  auditId: number;
  findingId: number;
  capaId: number;
  sidId: number;

  audit: Audit;
  finding: Finding;
  capa: Capa;
  sid: Sid;

  capaTypes = CapaECTypes;
  capaType: string;
  capaStatus = CapaStatus;

  IsApproved : boolean = false;

  evidenceTableData: TableData<Evidence>;
  forceReloadEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  hasInitialEvidences: boolean = false;

  evidenceTableOptions: TableOptions = {
    actionButtons: true,
    hideDeleteButton:true,
    columns: [
      {
        title: 'Evidence ID',
        fieldName: 'evidenceId',
      },
      {
        title: 'Evidence File',
        fieldName: 'fileName',
      },
      {
        title: 'Status',
        fieldName: 'status',
      },
      {
        title: 'Reason',
        fieldName: 'reason',
      },
      {
        title: 'Created By',
        fieldName: 'createdBy',
      },
      {
        title: 'Created Date',
        fieldName: 'createdOn',
        pipe: new DatePipe('en-US'),
        pipeParams: DISPLAY_DATE_FORMAT,
      },
      {
        title: 'Updated By',
        fieldName: 'modifiedBy',
      },
      {
        title: 'Updated Date',
        fieldName: 'modifiedOn',
        pipe: new DatePipe('en-US'),
        pipeParams: DISPLAY_DATE_FORMAT,
      },
    ],
    additionalInfo: [
      {
        title: 'Evidence Description',
        fieldName: 'description',
      },
    ],
  };

  breadcrumbs: Array<BreadcrumbItem> = [
    {
      title: 'Home',
      path: '/home',
    },
    {
      title: 'All audits',
      path: '/audit',
    },
  ];
  canAddEvidence: boolean;
  hasHistory : boolean = true;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private auditService: AuditService,
    private findingService: FindingService,
    private capaService: CapaService,
    private evidenceService: EvidenceService,
    private sidService: SidService,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    this.route.paramMap.pipe(
      switchMap((params: ParamMap): Observable<Capa> => {
        this.auditId = +params.get('auditId');
        this.findingId = +params.get('findingId');
        this.capaId = +params.get('capaId');
        this.sidId = +params.get('sidId');

        if (this.sidId > 0) {

          this.breadcrumbs = [
            {
              title: 'All SIDs',
              path: '/sid/list',
            }
          ];

          this.sidService.get(this.sidId)
            .subscribe(x => {
              this.sid = x;
              const b = {
                title: `SID: ${x.title}`,
                path: `/sid/${x.id}`,
              };
              this.breadcrumbs.push(b);
              this.breadcrumbs.push({
                title: 'Add/Edit Evidence',
                path: '',
              });
            });


        } else if (this.auditId > 0) {
          const b = {
            title: 'Audit:',
            path: `/audit/${this.auditId}`
          };
          this.breadcrumbs.push(
            {
              title: 'All audits',
              path: '/audit',
            });
          this.breadcrumbs.push(b);
          this.auditService.getAudit(this.auditId)
            .subscribe(a => {
              this.audit = a;
              b.title = `Audit: ${a.auditTitle}`;
            });
        } else {
          this.breadcrumbs.push(
            {
              title: 'All Findings',
              path: '/audit/all/findings',
            });
        }
        if (this.findingId > 0) {
        const f = {
          title: 'Finding:',
          path: `/audit/${this.auditId}/finding/${this.findingId}`,
        };
        this.breadcrumbs.push(f);
        this.findingService.getFinding(this.findingId, this.auditId)
          .subscribe(x => {
            this.finding = x;
            f.title = `Finding: ${x.findingTitle}`;
          });
        }

        return this.capaService.getCapa(this.capaId);
      })
    )
      .subscribe(v => {
        this.capa = v;
       
        this.capaType = this.capaTypes.find(x => x.key === v.capaEcType).text;
        this.breadcrumbs.push({
          title: `CAPA: ${v.capaTitle}`,
          path: '',
        });

        if (this.capa.status === CapaStatus.APPROVED) {
          this.IsApproved = true;
        }

        if (this.capa.status === CapaStatus.PENDING_FOR_EVIDENCE) {
          this.evidenceTableOptions.columns.splice(2, 2);
        }
      });
    this.refreshData();
  }

  ngAfterContentChecked(): void {
    this.evidenceTableOptions.columns[1].customTemplate = this.fileCellTemplate;
  }

  refreshData(): void {
    this.reloadEvidenceList()
      .subscribe(table => {
        this.evidenceTableData = table;
        this.hasHistory = table.hasHistory; 
        this.hasInitialEvidences = table.items.length > 0;
      });
  }
  refreshCapa(): void {
    this.capaService.getCapa(this.capaId)
      .subscribe(v => {
        this.capa = v;
        this.capaType = this.capaTypes.find(x => x.key === v.capaEcType).text;
      });
  }
  addEvidence(): void {
    const dialogRef = this.dialog.open(EvidenceAddDialogComponent, { data: { evidence: undefined, capaId: this.capaId } });

    dialogRef.afterClosed()
      .pipe(
        filter(res => res && res.success)
      )
      .subscribe(() => {
        
        if (!this.evidenceTableData || !this.evidenceTableData.totalCount) {
          this.reloadEvidenceList()
            .subscribe(table => {
              this.evidenceTableData = table;
              this.hasInitialEvidences = table.items.length > 0;
            });
          this.refreshCapa();
        }
        this.forceReloadEvent.emit(true);
      });
  }

  showHistory(): void {
    this.router.navigateByUrl(`/evidence/history/${this.capa.id}`); 
  }
  
  reloadEvidenceList(): Observable<TableDataEvidence<Evidence>> {
    return this.evidenceService.getEvidenceList(this.capaId)
      .pipe(
        tap(
          table => this.canAddEvidence = table.canAddEvidence,
          table => this.hasHistory = table.hasHistory
          )
      );
  }

  editEvidence(_id: number, data: Evidence): void {
    let copy;
    if (data) {
      copy = { ...data }
    }

    if(data.status != "Approved"){

      const dialogRef = this.dialog.open(EvidenceAddDialogComponent, { data: { evidence: copy, capaId: this.capaId } });
      dialogRef.afterClosed()
        .pipe(
          filter(res => res && res.success)
        )
        .subscribe(() => {
          this.refreshCapa();
          this.forceReloadEvent.emit(true);
        });
    }
  }
  
  deleteData(id: number): Observable<RequestResult> {
          
    let  test = this.evidenceService.deleteEvidence(id)
      .pipe(
        tap(() => this.refreshCapa())
      );

    return  test;  
  }

  submitEvidence(): void {
    if (!(this.capa.canSubmitEvidence)) {
      return;
    }
    const dlgData = {
      data: {
        title: 'Are you sure you want to submit all Evidences?',
        text: '',
        okText: 'Submit',
      },
    };
    // if (this.audit.draftFindingCount === 0) {
    dlgData.data.text =
      `<b>If you click the Submit button, no one will be able to add more evidences.</b><br> \
        <b>${this.evidenceTableData.totalCount} Evidences</b> are about to be submitted. \
      <br>You can't undo this action`;
    // }
    const dialogRef = this.dialog.open(OkCancelDialogComponent, dlgData);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.capaService.submitEvidences(this.capa.id)
          .subscribe(() => {
            if (this.audit) {
              this.router.navigateByUrl(`/audit/${this.auditId}/finding/${this.findingId}`);
            } else {
              this.router.navigateByUrl(`/sid/${this.sidId}`);
            }
          });
      }
    });
  }
}
